import { resolveRouteMetadata } from '../seo/routeMetadata';
import {
  classifyFormDescriptor,
  classifyLinkTarget,
  createRouteChangeTracker,
  createScrollDepthTracker,
} from './analyticsCore';

const DEFAULT_GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-MX3VHFLRCS';
const SECONDARY_GA4_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID_SECONDARY || 'G-5YGKXW2W0S';
const GA4_DESTINATION_IDS = Array.from(
  new Set([DEFAULT_GA4_MEASUREMENT_ID, SECONDARY_GA4_MEASUREMENT_ID].filter(Boolean)),
);
const CANONICAL_ANALYTICS_HOST = 'www.douglasmccartney.com';
const ANALYTICS_DEBUG_SESSION_KEY = 'analytics_debug_mode';

const CONSENT_KEYS = [
  'analytics_consent',
  'cookie_consent_analytics',
  'ga_consent',
  'consent_analytics',
];

const TRUTHY_VALUES = new Set(['1', 'true', 'yes', 'granted', 'allow', 'allowed']);
const FALSY_VALUES = new Set(['0', 'false', 'no', 'denied', 'deny', 'denied_all', 'blocked']);

function compactWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeSendToList(sendTo) {
  if (!sendTo) return [];
  if (Array.isArray(sendTo)) return sendTo.filter(Boolean);
  return [sendTo].filter(Boolean);
}

function safeStorageGet(storage, key) {
  try {
    return storage && typeof storage.getItem === 'function' ? storage.getItem(key) : null;
  } catch (_err) {
    return null;
  }
}

function safeStorageSet(storage, key, value) {
  try {
    if (storage && typeof storage.setItem === 'function') {
      storage.setItem(key, value);
    }
  } catch (_err) {
    // ignore storage errors
  }
}

function safeStorageRemove(storage, key) {
  try {
    if (storage && typeof storage.removeItem === 'function') {
      storage.removeItem(key);
    }
  } catch (_err) {
    // ignore storage errors
  }
}

function parseConsentValue(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  if (TRUTHY_VALUES.has(normalized)) return true;
  if (FALSY_VALUES.has(normalized)) return false;
  return null;
}

export function isAnalyticsDebugMode(windowObj = typeof window !== 'undefined' ? window : undefined) {
  if (!windowObj) return false;

  try {
    const params = new URLSearchParams(windowObj.location?.search || '');
    const queryValue = params.get('debug_analytics');
    if (queryValue === '1') {
      safeStorageSet(windowObj.sessionStorage, ANALYTICS_DEBUG_SESSION_KEY, '1');
      return true;
    }
    if (queryValue === '0') {
      safeStorageRemove(windowObj.sessionStorage, ANALYTICS_DEBUG_SESSION_KEY);
      return false;
    }
  } catch (_err) {
    // ignore malformed URL edge cases
  }

  return safeStorageGet(windowObj.sessionStorage, ANALYTICS_DEBUG_SESSION_KEY) === '1';
}

export function getAnalyticsBlockReason(windowObj = typeof window !== 'undefined' ? window : undefined) {
  if (!windowObj) return 'no_window';

  const gaDisableFlag = `ga-disable-${DEFAULT_GA4_MEASUREMENT_ID}`;
  if (windowObj[gaDisableFlag]) return 'ga_disabled_flag';
  if (windowObj.__analyticsDisabled === true) return 'analytics_disabled_flag';
  if (windowObj.__privacySettings && windowObj.__privacySettings.analytics === false) return 'privacy_settings_disabled';

  const dnt = String(
    windowObj.navigator?.doNotTrack ||
    windowObj.doNotTrack ||
    windowObj.navigator?.msDoNotTrack ||
    '',
  ).toLowerCase();
  if (dnt === '1' || dnt === 'yes') return 'do_not_track';

  for (const key of CONSENT_KEYS) {
    const raw = safeStorageGet(windowObj.localStorage, key);
    const parsed = parseConsentValue(raw);
    if (parsed === false) return `consent_denied:${key}`;
  }

  return null;
}

export function canSendAnalytics(windowObj = typeof window !== 'undefined' ? window : undefined) {
  return !getAnalyticsBlockReason(windowObj);
}

export function sendGa4Event(eventName, params = {}, options = {}) {
  const windowObj = options.windowObj || (typeof window !== 'undefined' ? window : undefined);
  if (!windowObj) return false;

  const debug = isAnalyticsDebugMode(windowObj);
  const blockReason = getAnalyticsBlockReason(windowObj);
  const basePayload = debug ? { ...params, debug_mode: true } : { ...params };
  const mergedSendTo = Array.from(
    new Set([...normalizeSendToList(basePayload.send_to), ...GA4_DESTINATION_IDS]),
  );
  const payload = mergedSendTo.length
    ? { ...basePayload, send_to: mergedSendTo }
    : basePayload;

  if (debug && typeof console !== 'undefined' && typeof console.info === 'function') {
    console.info('[Analytics QA]', {
      action: blockReason ? 'skip' : 'send',
      eventName,
      params: payload,
      reason: blockReason || (typeof windowObj.gtag === 'function' ? null : 'gtag_missing'),
    });
  }

  if (blockReason) return false;
  if (typeof windowObj.gtag !== 'function') return false;

  windowObj.gtag('event', eventName, payload);
  return true;
}

export function resolveAnalyticsRoute(pathname, search, hostname) {
  const meta = resolveRouteMetadata({
    pathname,
    search,
    hostname,
  });
  const prodMeta = resolveRouteMetadata({
    pathname,
    search,
    hostname: CANONICAL_ANALYTICS_HOST,
  });

  return {
    pathname,
    search,
    meta,
    indexable: Boolean(prodMeta && prodMeta.indexable),
  };
}

function getPageContext(windowObj, routeState) {
  const pageLocation = String(windowObj.location?.href || '');
  const pagePath = `${routeState.pathname || '/'}${routeState.search || ''}`;
  const pageTitle = compactWhitespace(routeState.title || windowObj.document?.title || '');
  return {
    page_location: pageLocation,
    page_path: pagePath,
    page_title: pageTitle,
  };
}

function getAnchorText(anchor) {
  return compactWhitespace(anchor?.getAttribute?.('aria-label') || anchor?.textContent || '');
}

function getScrollPercent(windowObj, documentObj) {
  const doc = documentObj?.documentElement;
  const body = documentObj?.body;
  if (!doc || !windowObj) return 0;

  const scrollTop = Number(windowObj.pageYOffset ?? doc.scrollTop ?? body?.scrollTop ?? 0);
  const viewportHeight = Number(windowObj.innerHeight ?? doc.clientHeight ?? 0);
  const scrollHeight = Math.max(
    Number(doc.scrollHeight || 0),
    Number(body?.scrollHeight || 0),
    Number(doc.offsetHeight || 0),
    Number(body?.offsetHeight || 0),
  );
  const maxScrollable = Math.max(scrollHeight - viewportHeight, 0);
  if (maxScrollable <= 0) return 0;
  return Math.max(0, Math.min(100, (scrollTop / maxScrollable) * 100));
}

function describeForm(form) {
  const controls = Array.from(form?.elements || []);
  const fieldNames = controls
    .map((el) => (el && typeof el.name === 'string' ? el.name : ''))
    .filter(Boolean);

  return {
    formId: form?.id || '',
    formName: form?.getAttribute?.('name') || '',
    className: typeof form?.className === 'string' ? form.className : '',
    method: String(form?.getAttribute?.('method') || form?.method || 'get').toLowerCase(),
    action: form?.getAttribute?.('action') || '',
    fieldNames,
  };
}

export function createAnalyticsRuntime(options = {}) {
  const windowObj = options.windowObj || (typeof window !== 'undefined' ? window : undefined);
  const documentObj = options.documentObj || (typeof document !== 'undefined' ? document : undefined);

  const routeTracker = createRouteChangeTracker({
    dedupeWindowMs: 1000,
    onPageView(routeState) {
      const pageContext = getPageContext(windowObj, routeState);
      sendGa4Event('page_view', {
        ...pageContext,
        seo_indexable: routeState.indexable ? 1 : 0,
      }, { windowObj });
    },
  });

  const scrollTracker = createScrollDepthTracker({
    thresholds: [25, 50, 75, 90],
    onThreshold({ threshold, pageKey }) {
      const routeState = runtime.getCurrentRouteState();
      if (!routeState || !routeState.indexable) return;
      sendGa4Event('scroll_depth', {
        percent_scrolled: threshold,
        page_path: pageKey,
      }, { windowObj });
    },
  });

  let currentRouteState = null;

  function setCurrentRouteState(routeState) {
    currentRouteState = routeState;
    scrollTracker.reset(`${routeState?.pathname || '/'}${routeState?.search || ''}`);
  }

  function getCurrentRouteState() {
    return currentRouteState;
  }

  function trackCurrentPageView() {
    if (!currentRouteState) return false;
    return routeTracker.track(currentRouteState);
  }

  function checkScrollDepth() {
    if (!windowObj || !documentObj || !currentRouteState || !currentRouteState.indexable) return [];
    const percent = getScrollPercent(windowObj, documentObj);
    return scrollTracker.update(percent, {
      pathname: currentRouteState.pathname,
      search: currentRouteState.search,
    });
  }

  function handleDocumentClick(event) {
    if (!windowObj || !documentObj) return;
    if (!currentRouteState || !currentRouteState.indexable) return;
    if (!event || event.defaultPrevented) return;
    if (typeof documentObj.querySelector !== 'function') return;
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const anchor = target.closest('a[href]');
    if (!anchor || anchor.dataset?.analyticsIgnore === '1') return;

    const href = anchor.getAttribute('href') || '';
    const classified = classifyLinkTarget({ href, currentOrigin: windowObj.location?.origin || '' });
    if (!classified) return;

    const pageContext = getPageContext(windowObj, currentRouteState);
    sendGa4Event(classified.eventName, {
      ...pageContext,
      link_url: href,
      link_domain: classified.host || '',
      link_type: classified.kind,
      link_text: getAnchorText(anchor),
    }, { windowObj });
  }

  function handleFormSubmit(event) {
    if (!windowObj || !documentObj) return;
    if (!currentRouteState || !currentRouteState.indexable) return;
    const form = event?.target;
    if (!form || typeof form.getAttribute !== 'function') return;
    if (form.dataset?.analyticsIgnore === '1') return;

    const descriptor = describeForm(form);
    const classification = classifyFormDescriptor({
      pathname: currentRouteState.pathname,
      formId: descriptor.formId,
      formName: descriptor.formName,
      className: descriptor.className,
      fieldNames: descriptor.fieldNames,
    });

    const pageContext = getPageContext(windowObj, currentRouteState);
    sendGa4Event(classification.eventName, {
      ...pageContext,
      form_id: descriptor.formId || '',
      form_name: descriptor.formName || '',
      form_method: descriptor.method || '',
      form_action: descriptor.action || '',
      form_type: classification.formType,
      lead_intent: classification.leadIntent || '',
      field_count: descriptor.fieldNames.length,
    }, { windowObj });
  }

  const runtime = {
    checkScrollDepth,
    getCurrentRouteState,
    handleDocumentClick,
    handleFormSubmit,
    setCurrentRouteState,
    trackCurrentPageView,
    windowObj,
    documentObj,
  };

  return runtime;
}

export function buildResolvedRouteState(pathname, search, hostname) {
  const resolved = resolveAnalyticsRoute(pathname, search, hostname);
  return {
    pathname: pathname || '/',
    search: search || '',
    indexable: Boolean(resolved.indexable),
    meta: resolved.meta,
    title: resolved.meta?.title || '',
  };
}
